// IDENTIFICADORES DE PESSOA E DE VINCULO — CPF e matricula, como os arquivos
// de folha os escrevem.
//
// E' a dimensao `identificadores` da matriz de cenarios: CPF com 11 digitos,
// CPF que perdeu os zeros a esquerda por ter viajado como NUMERO, CPF com
// mascara, matricula com digito verificador e matricula com sufixo. Cada um
// desses valores precisa de um leitor, e o leitor precisa morar num lugar so' —
// antes desta feature ele vivia dentro de `alece/alece-retorno.ts`, e o
// segundo layout que precisasse dele copiaria o calculo do digito.
//
// Copiar UM digito verificador e' como um CPF vira outro: as duas copias
// divergem na primeira correcao e ninguem percebe, porque as duas continuam
// devolvendo `true` para a maioria das entradas. Dai este modulo, no molde de
// `reconhecer.ts` — helper puro dos adaptadores, sem catalogo e sem I/O.
//
// `alece-retorno.ts` continua exportando `cpfValido` e `cpfCompletado`: a
// assinatura publica dele nao muda, so' o lugar onde a conta acontece.
/** So digitos — descarta ponto, traco, espaco e o que mais o export inventar. */
export function apenasDigitos(s) {
    return (s ?? "").replace(/\D/g, "");
}
/**
 * Digito verificador do CPF.
 *
 * Aqui ele importa de verdade: e' o que AUTORIZA completar zeros a esquerda de
 * um CPF que veio como numero. Sem a conferencia, completar seria adivinhar.
 */
export function cpfValido(cpf) {
    if (!/^\d{11}$/.test(cpf) || /^(\d)\1{10}$/.test(cpf))
        return false;
    const dv = (ate) => {
        let soma = 0;
        for (let i = 0; i < ate; i++)
            soma += Number(cpf[i]) * (ate + 1 - i);
        const r = (soma * 10) % 11;
        return r === 10 ? 0 : r;
    };
    return dv(9) === Number(cpf[9]) && dv(10) === Number(cpf[10]);
}
/**
 * CPF com 11 digitos, completado a esquerda, com o digito conferido.
 *
 * `null` quando nao ha digito, quando ha mais de 11, ou quando o verificador
 * nao fecha depois de completar — o adaptador descarta a linha com motivo, e
 * o ente corrige na origem.
 */
export function cpfCompletado(bruto) {
    const d = apenasDigitos(bruto);
    if (d.length === 0 || d.length > 11)
        return null;
    const cpf = d.padStart(11, "0");
    return cpfValido(cpf) ? { cpf, completado: d.length < 11 } : null;
}
/** O texto trazia mascara (ponto, traco, espaco) alem dos digitos? */
export function cpfComMascara(bruto) {
    const t = (bruto ?? "").trim();
    return t !== "" && t !== apenasDigitos(t);
}
/**
 * Digito verificador da matricula: modulo 11 com pesos 2..9 ciclicos, da
 * direita para a esquerda, resto subtraido de 11 e `0` quando o resultado
 * chega a 10 ou 11.
 *
 * E' a forma mais comum em cadastro funcional, e a que os layouts de teste
 * usam para exercitar `matricula-com-digito`. Nao ha "o" algoritmo de digito
 * de matricula — cada ente tem o seu —, entao ele e' DECLARADO pelo layout que
 * o usa, e nunca aplicado a matricula de layout que nao o declara.
 */
export function digitoDaMatricula(base) {
    let soma = 0;
    let peso = 2;
    for (let i = base.length - 1; i >= 0; i--) {
        soma += Number(base[i]) * peso;
        peso = peso === 9 ? 2 : peso + 1;
    }
    const d = 11 - (soma % 11);
    return String(d >= 10 ? 0 : d);
}
/** A matricula (base + digito) fecha no verificador? */
export function matriculaComDigitoValida(matricula) {
    if (!/^\d{2,}$/.test(matricula))
        return false;
    const base = matricula.slice(0, -1);
    return digitoDaMatricula(base) === matricula.slice(-1);
}
/**
 * Separa o sufixo da matricula (`1300509-A` → `1300509` + `A`).
 *
 * O sufixo distingue VINCULOS da mesma pessoa em parte dos cadastros; para a
 * Atlas a matricula e' a chave da folha, e carregar o sufixo faria a mesma
 * pessoa aparecer como duas. Quem remove REGISTRA o fato, porque o retorno do
 * ente vai continuar trazendo o sufixo e alguem vai precisar da ligacao.
 */
export function matriculaSemSufixo(bruto) {
    const m = /^\s*(\d+)-([A-Za-z0-9]{1,4})\s*$/.exec(bruto ?? "");
    if (!m)
        return { matricula: (bruto ?? "").trim(), sufixo: "" };
    return { matricula: m[1], sufixo: m[2] };
}
//# sourceMappingURL=identidade.js.map